/**
 * Outbound clipboard fan-out (`broadcastClip`) + the inbound shape guard (`isClipboardTextPayload`).
 * `broadcastClip` mirrors the CLI `clipSend`: per-recipient encrypt inside the E2E envelope (server never sees content), `origin.surface="pwa"`; content arrives as an arg (no sync web clipboard-read). `isClipboardTextPayload` is used by `services/notifications.ts` ONLY — the SW uses `clipOriginLabel` instead to avoid importing `api/client` and breaking SW bundle isolation.
 * SECURITY: clip `content` is decrypted plaintext — never logged; the fan-out logs counts/deviceIds only.
 */

import type { ClipboardPayload, SurfaceKind } from "@reflecto/core";
import { decodeBase64, encodeBase64, encryptMessage } from "@reflecto/core";
import { getDeviceKeys, sendClip } from "../api/client";
import { messages } from "../i18n";
import { logWarn } from "../lib/log";
import { addClip } from "./clipsStore";

/**
 * Recipient summary for a `BroadcastClipOptions.peerLabel` whose recipient is
 * the paired phone: the phone's own display name if it has sent one, else
 * the same fallback `HeaderBar` uses for its "Paired with X" line. Both
 * `ShareTargetPage` and `createWebClipboardCapabilities`
 * (`adapters/clipboard.web.ts`) call `broadcastClip` for this exact
 * recipient — `peerLabel` is REQUIRED with no fallback of its own (see its
 * doc comment below), so each call site must derive one. Shared here so the
 * fallback string can't drift between the two (nitpick, round 3).
 */
export function androidPeerLabel(androidDeviceName: string | undefined): string {
  return androidDeviceName ?? messages.header.pairedFallbackDevice;
}

/** Per-leg network timeout for the clip fan-out (directory fetch + each send). */
const CLIP_FETCH_TIMEOUT_MS = 15_000;

/**
 * Re-exported from `@reflecto/core` since desktop slice 4 (R4-c). This module used to define
 * the guard itself; it was one of three near-identical copies that had stopped agreeing.
 *
 * This copy's behaviour is the one that WON: it was the only one type-checking `sensitive`,
 * and the only one whose `origin.surface` check the others could not silently outlive. What
 * it loses is its hardcoded surface list — the shared guard derives that from an exhaustive
 * `Record<SurfaceKind, true>`, so a sixth surface is a compile error rather than a value this
 * file quietly rejects while extension and CLI accept it.
 */
export { isClipboardTextPayload } from "@reflecto/core";

/** Per-call outcome so the manual-send UI can report partial / total failure. */
export interface BroadcastClipResult {
  /** Number of partner devices the clip reached. */
  sent: number;
  /** Total partner devices the directory listed. */
  total: number;
  /** Device ids that failed to encrypt-or-send. */
  failed: string[];
}

export interface BroadcastClipOptions {
  /** Display name of THIS device (origin.label). Falls back to "Web". */
  originLabel?: string;
  /** Mark the clip sensitive (preview-mask hint on the receiver). */
  sensitive?: boolean;
  /**
   * Recipient summary for the outbound clip-history record (spec C4's
   * `peerLabel` "double duty" — the recipient summary for a sent entry).
   * REQUIRED — every caller must supply the actual known recipient name
   * (e.g. the paired phone's display name). There is no fallback: an
   * earlier revision defaulted to the send-toast string
   * (`messages.clipboard.sendSuccess(sent)`, e.g. "Sent to 1 device."),
   * which `ClipCard` then wrapped as "to Sent to 1 device." when rendering
   * the sent caption — a garbage caption. That fallback was already
   * unreachable (both call sites always passed an explicit label) but
   * stayed live in the type, so a future caller could silently reintroduce
   * it. Making this required turns that into a compile error instead.
   */
  peerLabel: string;
}

/**
 * Encrypt `content` per partner device and fan it out via
 * `sendClip` (`POST /v1/clipboard`). Returns a per-recipient outcome rather than
 * throwing on partial failure so the caller can surface "reached N/M".
 * Throws only when the prerequisites are missing (`not_paired`,
 * `empty`) or the directory fetch fails — i.e. cases where no send was
 * even attempted.
 *
 * `secretKey` is the caller's own base64 X25519 private key (read from
 * `webStorage` by the UI before calling, keeping this function free of
 * a storage dependency for testability).
 */
export async function broadcastClip(
  token: string | undefined,
  secretKeyB64: string | undefined,
  content: string,
  opts: BroadcastClipOptions
): Promise<BroadcastClipResult> {
  if (!token || !secretKeyB64) {
    throw new Error("not_paired");
  }
  if (!content.trim()) {
    throw new Error("empty");
  }

  const surface: SurfaceKind = "pwa";
  const payload: ClipboardPayload = {
    type: "clipboard",
    contentType: "text",
    content,
    timestamp: Date.now(),
    origin: { label: opts.originLabel ?? "Web", surface },
    provenance: "device",
    ...(opts.sensitive ? { sensitive: true } : {}),
  };
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const secretKey = decodeBase64(secretKeyB64);

  // Bound every network leg so a hung connection can't stall the fan-out indefinitely.
  const { devices } = await getDeviceKeys(token, AbortSignal.timeout(CLIP_FETCH_TIMEOUT_MS));

  let sent = 0;
  const failed: string[] = [];
  for (const dev of devices) {
    try {
      const wire = encryptMessage(plaintext, decodeBase64(dev.publicKey), secretKey);
      await sendClip(
        token,
        encodeBase64(wire),
        crypto.randomUUID(),
        dev.deviceId,
        AbortSignal.timeout(CLIP_FETCH_TIMEOUT_MS)
      );
      sent++;
    } catch (err) {
      // Never log the clip content — only the device id + error.
      logWarn("clipboard", "clip_send_to_device_failed", err, { deviceId: dev.deviceId });
      failed.push(dev.deviceId);
    }
  }

  // C7: record exactly ONE outbound history entry per send event — never
  // per recipient — guarded on at least one device having actually been
  // reached (mirrors Android's `enqueued > 0` guard in
  // `ClipboardSendCoordinator`; a broadcast that reached nobody isn't a
  // "sent" clip). `peerDeviceId` is left undefined — v1 is broadcast-to-all
  // (spec §7), so no single recipient id applies.
  //
  // Awaited (not fire-and-forget): `createClipHistory.add()` captures its
  // write generation AFTER an internal `await hydrate()` — a `clearClips()`
  // (unpair/pairing-lost) landing in that gap would otherwise persist this
  // sent entry AFTER the wipe, resurrecting plaintext clip content post-clear
  // (C5). Awaiting here closes that window; `add()` never rejects, so this
  // can't turn a partial-success send into a thrown error.
  if (sent > 0) {
    await addClip({
      id: crypto.randomUUID(),
      direction: "sent",
      content,
      peerLabel: opts.peerLabel,
      provenance: payload.provenance,
      sensitive: opts.sensitive,
      timestamp: payload.timestamp,
    });
  }

  return { sent, total: devices.length, failed };
}
